import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ScrollText, Users2, UserCheck, Folder, FolderOpen, ChevronRight,
  GraduationCap, Archive, LogOut, Info, Plus, ShieldCheck, ClipboardList,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { isStaffUser, hasRole, rolesLabel } from "@/lib/constants";

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-1 rounded-md text-[13px] transition-colors duration-200 ${
    isActive ? "bg-white/20 text-white font-medium" : "text-white/80 hover:bg-white/10"
  }`;

const linkClsSub = ({ isActive }) =>
  `flex items-center gap-2.5 pl-7 pr-3 py-1 rounded-md text-[13px] transition-colors duration-200 ${
    isActive ? "bg-white/20 text-white font-medium" : "text-white/80 hover:bg-white/10"
  }`;

const secBtnCls =
  "flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/50 hover:text-white/80 transition-colors";

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState({});
  const [sec, setSec] = useState({ admin: true, verwaltung: true, kurse: true });
  const toggleSec = (k) => setSec((s) => ({ ...s, [k]: !s[k] }));

  const load = () => api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener("courses-changed", onChange);
    return () => window.removeEventListener("courses-changed", onChange);
  }, []);

  const staff = isStaffUser(user);
  const active = courses.filter((c) => !c.expired);
  const expired = courses.filter((c) => c.expired);

  const groups = {};
  active.forEach((c) => { (groups[c.group] = groups[c.group] || []).push(c); });

  const toggle = (g) => setOpen((o) => ({ ...o, [g]: !o[g] }));

  return (
    <aside className="w-[260px] shrink-0 bg-[#3d5a75] h-screen md:sticky top-0 flex flex-col" data-testid="sidebar">
      <div className="px-3 pt-4 pb-1">
        <img src="/logo-katechese-white.png" alt="katechese.online" className="w-full h-auto object-contain block" data-testid="app-logo" />
      </div>
      <nav onClick={(e) => { if (e.target.closest("a")) onNavigate?.(); }} className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        <NavLink to="/" end className={linkCls} data-testid="nav-dashboard">
          <LayoutDashboard size={20} /> Übersicht
        </NavLink>

        {hasRole(user, "admin") && (
          <div className="pt-3">
            <button onClick={() => toggleSec("admin")} className={`w-full px-3 pb-1 ${secBtnCls}`} data-testid="section-admin">
              <ChevronRight size={12} className={`transition-transform duration-200 ${sec.admin ? "rotate-90" : ""}`} /> Administrator
            </button>
            {sec.admin && (
              <>
                <NavLink to="/weblog" className={linkClsSub} data-testid="nav-weblog">
                  <ScrollText size={18} /> Weblog
                </NavLink>
                <NavLink to="/benutzer" className={linkClsSub} data-testid="nav-users">
                  <Users2 size={18} /> User
                </NavLink>
                <NavLink to="/teilnehmer" className={linkClsSub} data-testid="nav-teilnehmer">
                  <UserCheck size={18} /> Teilnehmer
                </NavLink>
              </>
            )}
          </div>
        )}

        {(hasRole(user, "user-verwaltung") || hasRole(user, "admin")) && (
          <div className="pt-3">
            <button onClick={() => toggleSec("verwaltung")} className={`w-full px-3 pb-1 ${secBtnCls}`} data-testid="section-verwaltung">
              <ChevronRight size={12} className={`transition-transform duration-200 ${sec.verwaltung ? "rotate-90" : ""}`} /> Verwaltung
            </button>
            {sec.verwaltung && (
              <>
                <NavLink to="/verwaltung/teilnehmer" className={linkClsSub} data-testid="nav-verwaltung-teilnehmer">
                  <UserCheck size={18} /> Teilnehmer
                </NavLink>
                <NavLink to="/verwaltung/datenschutz" className={linkClsSub} data-testid="nav-verwaltung-datenschutz">
                  <ShieldCheck size={18} /> Datenschutzerklärungen
                </NavLink>
                <NavLink to="/verwaltung/formulare" className={linkClsSub} data-testid="nav-verwaltung-formulare">
                  <ClipboardList size={18} /> Anmeldeformulare
                </NavLink>
              </>
            )}
          </div>
        )}

        <div className="pt-3">
          <div className="flex items-center justify-between px-3 pb-1">
            <button onClick={() => toggleSec("kurse")} className={secBtnCls} data-testid="section-kurse">
              <ChevronRight size={12} className={`transition-transform duration-200 ${sec.kurse ? "rotate-90" : ""}`} /> Kurse
            </button>
            {staff && (
              <NavLink to="/kurse" className="text-[11px] text-white/90 hover:underline" data-testid="nav-courses-all">
                Alle
              </NavLink>
            )}
          </div>

          {sec.kurse && (staff ? (
            <>
              <NavLink to="/kurse?neu=1" className={linkClsSub} data-testid="nav-new-course">
                <Plus size={16} /> Neuen Kurs anlegen
              </NavLink>
              {Object.keys(groups).sort().map((g) => (
                <div key={g}>
                  <button
                    onClick={() => toggle(g)}
                    className="w-full flex items-center gap-2 pl-6 pr-3 py-1 rounded-md text-[13px] text-white/90 hover:bg-white/10"
                    data-testid={`course-folder-${g}`}
                  >
                    {open[g] ? <FolderOpen size={16} /> : <Folder size={16} />}
                    <span className="font-medium">{g}</span>
                    <ChevronRight size={14} className={`ml-auto transition-transform duration-200 ${open[g] ? "rotate-90" : ""}`} />
                  </button>
                  {open[g] && groups[g].map((c) => (
                    <NavLink key={c.id} to={`/kurs/${c.id}`} className={linkCls} style={{ paddingLeft: 44 }} data-testid={`course-link-${c.id}`}>
                      <GraduationCap size={16} /> <span className="truncate">{c.kurs_name}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
              {expired.length > 0 && (
                <div className="pt-2">
                  <button onClick={() => toggle("__exp")} className="w-full flex items-center gap-2 pl-6 pr-3 py-1 rounded-md text-[13px] text-white/70 hover:bg-white/10" data-testid="course-folder-expired">
                    <Archive size={16} /> <span>Abgelaufene Kurse</span>
                    <ChevronRight size={14} className={`ml-auto transition-transform duration-200 ${open["__exp"] ? "rotate-90" : ""}`} />
                  </button>
                  {open["__exp"] && expired.map((c) => (
                    <NavLink key={c.id} to={`/kurs/${c.id}`} className={linkCls} style={{ paddingLeft: 44 }} data-testid={`course-link-${c.id}`}>
                      <GraduationCap size={16} /> <span className="truncate">{c.kurs_name}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </>
          ) : (
            courses.map((c) => (
              <NavLink key={c.id} to={`/kurs/${c.id}`} className={linkClsSub} data-testid={`course-link-${c.id}`}>
                <GraduationCap size={16} /> <span className="truncate">{c.kurs_name}</span>
              </NavLink>
            ))
          ))}
        </div>

        <div className="pt-4">
          <NavLink to="/impressum" className={linkCls} data-testid="nav-impressum">
            <Info size={20} /> Impressum
          </NavLink>
        </div>
      </nav>

      <div className="border-t border-white/15 p-3">
        <div className="px-3 py-2">
          <p className="text-sm font-medium truncate text-white" data-testid="current-user-email">{user?.email}</p>
          <p className="text-xs text-white/60">{rolesLabel(user)}</p>
        </div>
        <button
          onClick={() => { logout(); navigate("/login"); }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/80 hover:bg-white/10"
          data-testid="logout-button"
        >
          <LogOut size={18} /> Abmelden
        </button>
      </div>
    </aside>
  );
}
